import { Observable, of, timer } from 'rxjs';
import { catchError, exhaustMap, map, scan, takeWhile, timeout } from 'rxjs/operators';
import { ICoCoachVraagStatus } from '../../interfaces/co-coach.interface';

/**
 * Een vraag aan de co-coach loopt op de achterkant door; de webapp vraagt
 * elke seconde hoe ver hij is (GET /CoCoach/Vraag/{id}) en toont intussen
 * de stappen. Stoppen: bij klaar of fout, als het paneel dichtgaat
 * (afmelden) of na twee minuten.
 */
export const PEIL_MS = 1000;
export const MAX_WACHT_MS = 120_000;
/** Zoveel mislukte aanvragen op rij en het is een fout (de vraag is weg). */
export const MAX_FOUTEN_OP_RIJ = 5;
/** Eén aanvraag mag niet langer hangen dan dit; dan telt hij als mislukt. */
const AANVRAAG_MS = 15_000;

export type PeilUitkomst = 'verder' | 'klaar' | 'fout' | 'te-lang';

export interface PeilBericht {
  /** Het laatste antwoord van de server; null als de aanvraag mislukte. */
  status: ICoCoachVraagStatus | null;
  uitkomst: PeilUitkomst;
}

/** Wat te doen na een antwoord (of een mislukte aanvraag, `status` null). */
export function peilStap(
  status: ICoCoachVraagStatus | null,
  verstreken: number,
  foutenOpRij = 0,
  max = MAX_WACHT_MS,
): PeilUitkomst {
  if (status?.status === 'klaar') {
    return 'klaar';
  }
  if (status?.status === 'fout' || foutenOpRij >= MAX_FOUTEN_OP_RIJ) {
    return 'fout';
  }
  return verstreken >= max ? 'te-lang' : 'verder';
}

export interface PeilOpties {
  interval?: number;
  max?: number;
  /** Wanneer de vraag gesteld is (ms); bij hervatten telt de tijd van toen. */
  gestart?: number;
  nu?: () => number;
}

/**
 * Peilen tot klaar, fout of te lang. Geeft elk antwoord door (voor de
 * stappen) en sluit af met het bericht waarin de uitkomst staat. Een nieuwe
 * aanvraag gaat pas weg als de vorige terug is.
 */
export function peil(haal: () => Observable<ICoCoachVraagStatus>, opties: PeilOpties = {}): Observable<PeilBericht> {
  const nu = opties.nu ?? (() => Date.now());
  const gestart = opties.gestart ?? nu();
  const max = opties.max ?? MAX_WACHT_MS;
  return timer(0, opties.interval ?? PEIL_MS).pipe(
    exhaustMap(() => haal().pipe(
      timeout(AANVRAAG_MS),
      catchError(() => of(null as ICoCoachVraagStatus | null)),
    )),
    scan((vorige, status) => ({ status, fouten: status ? 0 : vorige.fouten + 1 }), { status: null as ICoCoachVraagStatus | null, fouten: 0 }),
    map(({ status, fouten }): PeilBericht => ({ status, uitkomst: peilStap(status, nu() - gestart, fouten, max) })),
    takeWhile((bericht) => bericht.uitkomst === 'verder', true),
  );
}
